import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useMemo } from 'react';
import { Control, useWatch } from 'react-hook-form';

import AutocompleteField from '~components/FormFields/v2/Autocomplete/AutocompleteField';
import { AutocompleteFieldOption } from '~components/FormFields/v2/Autocomplete/types';
import { CSVColumn, CSVFileHeaders } from '~utils/csv/csvProcessor';

import { EMPTY_CELL_PLACEHOLDER } from './constants';
import { CSVColumnLabelCell } from './CSVColumnLabelCell';

export type CSVColumnMappingFormValues = Record<
  string,
  AutocompleteFieldOption<string> | null
>;

export const toHeaderOption = (header: string) => ({
  _entity: header,
  label: header,
  value: header,
});

interface CSVColumnMappingProps {
  columns: CSVColumn[];
  control: Control<CSVColumnMappingFormValues>;
  fileHeaders: CSVFileHeaders;
  fileName: string;
}

export function CSVColumnMapping({
  columns,
  control,
  fileHeaders,
  fileName,
}: CSVColumnMappingProps) {
  const selectedOptions = useWatch({ control });

  const headerOptions = useMemo(
    () => fileHeaders.headers.map(toHeaderOption),
    [fileHeaders.headers],
  );

  const mappedHeaders = new Set(
    Object.values(selectedOptions).map((option) => option?.value),
  );
  const ignoredHeaders = fileHeaders.headers.filter(
    (header) => !mappedHeaders.has(header),
  );

  return (
    <Box>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        {/* React escapes at render; i18next's escaping would show "&" as "&amp;" */}
        {t('csv_import_export.column_mapping.description', {
          fileName,
          interpolation: { escapeValue: false },
        })}
      </Typography>

      <TableContainer
        sx={{ maxHeight: 420, border: 1, borderColor: 'divider', borderRadius: 1 }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>
                {t('csv_import_export.field_reference.column')}
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>
                {t('csv_import_export.column_mapping.file_header')}
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>
                {t('csv_import_export.column_mapping.sample_value')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {columns.map((column) => {
              const selectedHeader = selectedOptions[column.key]?.value;

              return (
                <TableRow key={column.key}>
                  <CSVColumnLabelCell column={column} />
                  <TableCell sx={{ minWidth: 240 }}>
                    <AutocompleteField
                      control={control}
                      name={column.key}
                      options={headerOptions}
                    />
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                    {(selectedHeader && fileHeaders.sampleValues[selectedHeader]) ||
                      EMPTY_CELL_PLACEHOLDER}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {ignoredHeaders.length > 0 && (
        <Box
          sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 2 }}
        >
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('csv_import_export.column_mapping.ignored_headers')}
          </Typography>
          {ignoredHeaders.map((header) => (
            <Chip key={header} label={header} size="small" variant="outlined" />
          ))}
        </Box>
      )}
    </Box>
  );
}
